import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "@tanstack/react-router";
import {
  Award,
  Briefcase,
  Building2,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  GaugeCircle,
  GitCompareArrows,
  GraduationCap,
  HeartPulse,
  HelpCircle,
  LayoutDashboard,
  Lightbulb,
  Megaphone,
  Network,
  PanelLeft,
  PanelLeftClose,
  Settings,
  SlidersHorizontal,
  Target,
  Users,
  UsersRound,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAppSettings } from "@/hooks/use-app-settings";
import { useMyPerson } from "@/hooks/use-persons";
import { useSession } from "@/lib/auth-client";
import { canViewAll, showsTeamNav } from "@/lib/permissions";
import { appVersionKeys } from "@/lib/query";
import { cn } from "@/lib/utils";
import { useSidebar } from "./SidebarContext";

interface NavItem {
  label: string;
  icon: typeof LayoutDashboard;
  href: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const COLLAPSED_GROUPS_KEY = "sidebar-collapsed-groups";

// Read the persisted per-group collapse state, narrowing the parsed JSON to a
// plain string→boolean map (no `any`, no casts). Any malformed value is ignored.
function readCollapsedGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSED_GROUPS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const result: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "boolean") result[key] = value;
    }
    return result;
  } catch {
    return {};
  }
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [collapsedGroups, setCollapsedGroups] =
    useState<Record<string, boolean>>(readCollapsedGroups);
  const { data: session } = useSession();
  const { data: me } = useMyPerson();
  const location = useLocation();
  const { isMobile, mobileOpen, setMobileOpen } = useSidebar();

  // App version for the footer "What's new" link. Only changes on deploy (which
  // reloads the app), so it never needs to re-fetch within a session.
  const { data: appVersion } = useQuery({
    queryKey: appVersionKeys.all,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async (): Promise<string> => {
      const res = await fetch("/api/app-version");
      if (!res.ok) return "";
      const json = await res.json();
      return typeof json?.version === "string" ? json.version : "";
    },
  });

  // Which optional sections are switched on for this workspace. Read from the
  // server rather than a compiled constant: `build.ts` defines NODE_ENV as
  // "production" for every bundle, so a bundle-time gate would hide Reviews
  // from the sandbox too. See `docs/performance-reviews-design.md`.
  const { data: features } = useAppSettings();
  const showReviews = features?.reviewsEnabled === true;

  const navGroups = useMemo<NavGroup[]>(() => {
    const isAdmin = canViewAll(session?.user?.role);
    // The aspirations report is for admins and managers (anyone with reports).
    const showAspirations = isAdmin || me?.isManager === true;
    return [
      {
        label: "My Workspace",
        items: [
          { label: "My Dashboard", icon: LayoutDashboard, href: "/dashboard/my" },
          { label: "My Certifications", icon: Award, href: "/dashboard/my/certifications" },
          { label: "Help", icon: HelpCircle, href: "/dashboard/help" },
        ],
      },
      // Its own top-level section rather than an item under My Workspace: it
      // spans personal work (your own reviews), manager work (your reports')
      // and HR work (cycles, reports), so it belongs to none of the existing
      // groups. Whole group hidden until an admin switches it on at Settings →
      // Config → Feature availability — not a security boundary, every
      // /api/reviews* route 404s when the flag is off.
      //
      // Named "Feedback" (Matt, 2026-09-18) while the route, the page and the
      // whole code namespace stay `review`/`reviews` — the same deliberate
      // decoupling as the Ops→Git page and the Capacity report, both of which
      // kept their code names. NOTE the label collides with the floating
      // "Feedback" button and Settings → Feedback, which are the in-app
      // bug/feature widget (DM-WKF-002) and a different concern entirely.
      ...(showReviews
        ? [
            {
              label: "Feedback",
              items: [
                { label: "Reviews", icon: ClipboardList, href: "/dashboard/reviews" },
                // Admin-only: a form is HR configuration, and the route guards
                // the view tier on top of the feature flag.
                ...(isAdmin
                  ? [
                      {
                        label: "Review Forms",
                        icon: ClipboardList,
                        href: "/dashboard/reviews/forms",
                      },
                    ]
                  : []),
              ],
            } satisfies NavGroup,
          ]
        : []),
      {
        label: "People & Org",
        items: [
          { label: "People", icon: Users, href: "/dashboard/persons" },
          { label: "Roles", icon: Briefcase, href: "/dashboard/roles" },
          { label: "Organization", icon: Building2, href: "/dashboard/organization" },
          { label: "Org Chart", icon: Network, href: "/dashboard/organization/chart" },
          // Who is off, company-wide. Moved out of Administration and ungated on
          // 2026-09-22: booked leave is everyone's to see, and this is where
          // someone looks for it. The page still withholds the pending queue and
          // the reasons on it from anyone outside the admin tier.
          { label: "PTO Report", icon: CalendarDays, href: "/dashboard/pto" },
          // Shown to anyone holding the manager role (even with no reports yet —
          // the page's empty state explains that) and to admins who actually
          // have reports. An admin who manages nobody has no team to look at
          // and reaches any manager's team from that manager's profile.
          ...(showsTeamNav(session?.user?.role, me?.hasDirectReports === true)
            ? [{ label: "My Team", icon: UsersRound, href: "/dashboard/team" } satisfies NavItem]
            : []),
          ...(showAspirations
            ? [
                {
                  label: "Role Aspirations",
                  icon: Target,
                  href: "/dashboard/aspirations",
                } satisfies NavItem,
              ]
            : []),
        ],
      },
      {
        // Skills/Competencies/Certifications are the "Capability" area — the
        // detail-page breadcrumbs already label them that way; the nav now
        // matches. Compare Roles (career-path) lives here too as a growth tool.
        label: "Capability",
        items: [
          { label: "Skills", icon: GraduationCap, href: "/dashboard/skills" },
          { label: "Competencies", icon: Lightbulb, href: "/dashboard/competencies" },
          { label: "Certifications", icon: Award, href: "/dashboard/certifications" },
          // Role requirements, and where people fall short of them — EPIC-023.
          // The matrix lives here as a tab; it used to be its own entry.
          { label: "Capabilities", icon: Target, href: "/dashboard/capability-gaps" },
          { label: "Compare Roles", icon: GitCompareArrows, href: "/dashboard/roles/compare" },
        ],
      },
      {
        label: "Administration",
        items: [
          // Admin-only company dashboard (org health + company-wide certs).
          ...(isAdmin
            ? [
                {
                  label: "Org Health",
                  icon: HeartPulse,
                  href: "/dashboard/org-health",
                } satisfies NavItem,
              ]
            : []),
          // NOS's per-person capacity and target utilization. Read-only, and
          // admin-only for the same reason as the two above — company-wide.
          ...(isAdmin
            ? [
                {
                  label: "Capacity",
                  icon: GaugeCircle,
                  href: "/dashboard/capacity",
                } satisfies NavItem,
              ]
            : []),
          // Who is familiar with which industries and technologies (EPIC-043).
          // It lived under Capability while it was open to everyone; made
          // admin-only 2026-09-03 so the rating filter behaves the same for
          // everyone who can open it, which moved it in here with the other
          // company-wide reports.
          ...(isAdmin
            ? [
                {
                  label: "Constraints",
                  icon: SlidersHorizontal,
                  href: "/dashboard/constraints",
                } satisfies NavItem,
              ]
            : []),
          // Admin-only, on the same view tier as the route's own guard. It used
          // to render for everyone, greyed out with a "Soon" badge, which said
          // the wrong thing twice: the page is not coming soon, it is simply
          // not theirs, and a dead row invites a click that can never work.
          ...(isAdmin
            ? [
                {
                  label: "Settings",
                  icon: Settings,
                  href: "/dashboard/settings",
                } satisfies NavItem,
              ]
            : []),
        ],
      },
      // Every item in Administration is admin-gated, so a plain user or a
      // manager now leaves it with nothing in it — and the heading renders
      // whether or not it has items. Drop empty groups rather than special-
      // casing this one, so the next gated item cannot reintroduce a bare
      // heading.
    ].filter((group) => group.items.length > 0);
  }, [session?.user?.role, me?.isManager, me?.hasDirectReports, showReviews]);

  // Longest-prefix match with a path boundary. A naked `startsWith` would
  // light up any nav item whose href is a prefix of the current path — so
  // /dashboard/my/certifications lit up both "My Dashboard" (/dashboard/my)
  // and "My Certifications". Pick the longest matching href and let it win.
  const activeHref = useMemo(() => {
    const path = location.pathname;
    let best = "";
    for (const group of navGroups) {
      for (const item of group.items) {
        const matches = path === item.href || path.startsWith(`${item.href}/`);
        if (matches && item.href.length > best.length) best = item.href;
      }
    }
    return best;
  }, [location.pathname, navGroups]);

  function handleNavClick() {
    if (isMobile) {
      setMobileOpen(false);
    }
  }

  function toggleGroup(label: string) {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [label]: !prev[label] };
      try {
        localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify(next));
      } catch {
        // localStorage unavailable (private mode / quota) — state still lives in memory.
      }
      return next;
    });
  }

  // On mobile, don't show collapsed state — always show full sidebar
  const showLabels = isMobile ? true : !collapsed;

  const sidebarContent = (
    <aside
      className={cn(
        "flex flex-col border-r border-sidebar-border bg-sidebar transition-all duration-200",
        isMobile ? "w-64 h-full" : collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Brand */}
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
        {showLabels && (
          <div className="flex flex-col leading-none">
            <span className="font-display text-lg font-bold text-sidebar-foreground">Enable</span>
            <span className="mt-1 text-3xs font-semibold uppercase tracking-[0.14em] text-sidebar-foreground/45">
              NOS2 · Nymbl
            </span>
          </div>
        )}
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-sidebar-foreground/70 hover:text-sidebar-foreground shrink-0"
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2.5">
        {navGroups.map((group) => {
          // Per-group collapse only applies when labels are showing. In the
          // icon-only rail (showLabels false) every group stays expanded.
          const isGroupCollapsed = showLabels && collapsedGroups[group.label] === true;

          return (
            <div key={group.label} className="mb-1">
              {showLabels && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  aria-expanded={!isGroupCollapsed}
                  className="flex w-full items-center gap-1.5 px-2.5 pb-1.5 pt-4 text-3xs font-bold uppercase tracking-[0.14em] text-sidebar-foreground/40 transition-colors hover:text-sidebar-foreground/70"
                >
                  <ChevronDown
                    className={cn(
                      "h-3 w-3 shrink-0 transition-transform duration-150",
                      isGroupCollapsed && "-rotate-90",
                    )}
                  />
                  <span>{group.label}</span>
                </button>
              )}
              {!isGroupCollapsed &&
                group.items.map((item) => {
                  const isActive = item.href === activeHref;

                  return (
                    <Link
                      key={item.label}
                      to={item.href}
                      onClick={handleNavClick}
                      className={cn(
                        "relative flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] font-medium transition-colors duration-150",
                        isActive
                          ? "bg-sidebar-accent text-sidebar-primary"
                          : "text-sidebar-foreground/70",
                        !isActive && "hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
                      )}
                    >
                      {isActive && (
                        <span className="absolute -left-2.5 top-1.5 bottom-1.5 w-0.5 rounded-r bg-[var(--nymbl-g-cyan)]" />
                      )}
                      <item.icon className="h-4 w-4 shrink-0" />
                      {showLabels && <span>{item.label}</span>}
                    </Link>
                  );
                })}
            </div>
          );
        })}
      </nav>

      {/* Version → What's new */}
      <div className="border-t border-sidebar-border p-2">
        <Link
          to="/dashboard/whats-new"
          onClick={handleNavClick}
          title={appVersion ? `What's New — version ${appVersion}` : "What's New"}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2 text-xs font-medium transition-colors",
            location.pathname === "/dashboard/whats-new"
              ? "bg-sidebar-accent text-sidebar-primary"
              : "text-sidebar-foreground/50 hover:bg-sidebar-accent hover:text-sidebar-foreground",
          )}
        >
          <Megaphone className="h-4 w-4 shrink-0" />
          {showLabels && <span>What&rsquo;s new{appVersion ? ` · v${appVersion}` : ""}</span>}
        </Link>
      </div>
    </aside>
  );

  // Mobile: overlay sidebar
  if (isMobile) {
    if (!mobileOpen) return null;

    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} />
        {/* Slide-out sidebar */}
        <div className="fixed left-0 top-0 z-50 h-full">{sidebarContent}</div>
      </>
    );
  }

  // Desktop: normal sidebar
  return sidebarContent;
}
